import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Logo } from '@/components/auth/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { HttpClient } from '@/api/services/http/client';

const SetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    const userId = searchParams.get('userId');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const { toast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token || !emailParam || !userId) {
            setError('Invalid or expired link. Please request a new invitation.');
        }
    }, [token, emailParam, userId]);

    const validatePassword = () => {
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return false;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        setError('');
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validatePassword()) return;

        setLoading(true);
        try {
            const response = await HttpClient.apiClient.post('/user/set-password', {
                token,
                email: emailParam,
                userId,
                password,
                confirmPassword: confirmPassword,
            });

            if (response.data?.status === 'success') {
                const accessToken = response.data?.data?.accessToken?.token;
                if (accessToken) {
                    localStorage.setItem('token', accessToken);
                    if (userId) localStorage.setItem('userId', userId);
                }
                toast({
                    title: 'Password set successfully',
                    description: 'You have been logged in. Redirecting...',
                });
                setSuccess(true);
                setTimeout(() => {
                    navigate('/home');
                }, 2000);
            } else {
                throw new Error(response.data?.message || 'Failed to set password');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Failed to set password. The link may be invalid or expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full gradient-background flex items-center justify-center p-6 md:p-8">
            <div className="w-full max-w-md auth-card p-8">
                <div className="mb-8">
                    <Logo />
                </div>
                <h1 className="text-2xl font-bold mb-6">Set your password</h1>

                {error && (
                    <div className="p-4 bg-red-50 text-red-800 rounded-md mb-4">{error}</div>
                )}

                {success ? (
                    <div className="space-y-4">
                        <div className="p-4 bg-green-50 text-green-800 rounded-md flex items-center">
                            <Check className="h-5 w-5 mr-2" />
                            Password set successfully! Redirecting...
                        </div>
                        <Button asChild variant="outline" className="w-full">
                            <Link to="/sign-in">
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Sign In
                            </Link>
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input value={emailParam || ''} disabled className="bg-gray-100" />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                placeholder="••••••••"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                placeholder="••••••••"
                            />
                        </div>

                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Setting Password...
                                </>
                            ) : (
                                'Set Password'
                            )}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SetPassword; 